import { PREFECTURE_NAMES } from '~/constants';
import i18n from '~/locales/i18next';

/**
 * utf8mb4をはじくためのバリデーションロジック
 * 全角入力できる箇所は基本全て適用
 * @param {String}
 * @returns {Bool}
 */
export const validateUtf8mb4 = (value = '') => {
  // 通常のlengthでUTF-16のコードユニットの数
  const codeUnitLength = value.length;
  // 文字列としての長さ
  const { length } = [...value];
  return length === codeUnitLength;
};

export const surveyTitleValidationRule = {
  required: i18n.t('validation.required'),
  maxLength: {
    value: 70,
    message: i18n.t('validation.maxLengthText', {
      length: 70,
    }),
  },
  validate: {
    utf8mb4: (value) => validateUtf8mb4(value) || i18n.t('validation.includeUtf8mb4'),
  },
};

export const originalFormSurveyTitleValidationRule = {
  required: i18n.t('validation.required'),
  maxLength: {
    value: 50,
    message: i18n.t('validation.maxLengthText', {
      length: 50,
    }),
  },
  validate: {
    utf8mb4: (value) => validateUtf8mb4(value) || i18n.t('validation.includeUtf8mb4'),
  },
};

export const surveyUrlValidationRule = {
  required: i18n.t('validation.required'),
  pattern: {
    value: /^(https:\/\/|\.)\S*/,
    message: i18n.t('validation.beginningWithHttps'),
  },
  maxLength: {
    value: 1000,
    message: i18n.t('validation.maxLengthText', {
      length: 1000,
    }),
  },
  validate: {
    utf8mb4: (value) => validateUtf8mb4(value) || i18n.t('validation.includeUtf8mb4'),
    goggleAddOnUrl: (value) =>
      value?.match(/<EncryptID>/g) !== null || i18n.t('validation.notGoggleAddOnUrl'),
    format: (value) =>
      value
        ?.replace('<EncryptID>', '')
        .match(
          /^((https:\/\/)(%[0-9A-Fa-f]{2}|[-()_.!~*';/?:@&=+#$,A-Za-z0-9])+)([).!';/?:,][[:blank:|:blank:]])?$/
        ) !== null || i18n.t('validation.includeProhibitedCharacters'),
  },
};

export const numberOfQuestionsValidationRule = {
  required: i18n.t('validation.required'),
  pattern: {
    value: /^[0-9\b]+$/,
    message: i18n.t('validation.invalidFormat'),
  },
  min: {
    value: 1,
    message: i18n.t('validation.minNumber', {
      min: 1,
    }),
  },
  maxLength: {
    value: 2,
    message: i18n.t('validation.maxLength', {
      length: 2,
    }),
  },
};

export const targetSamplesValidationRule = {
  required: i18n.t('validation.required'),
  pattern: {
    value: /^[0-9\b]+$/,
    message: i18n.t('validation.invalidFormat'),
  },
  min: {
    value: 1,
    message: i18n.t('validation.minNumber', {
      min: 1,
    }),
  },
};

export const emailValidationRule = {
  required: i18n.t('validation.required'),
  maxLength: {
    value: 100,
    message: i18n.t('validation.invalidEmailFormat'),
  },
  pattern: {
    value: /^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]+\.[A-Za-z0-9]+$/,
    message: i18n.t('validation.invalidFormat'),
  },
};

export const adminEmailValidationRule = {
  required: i18n.t('validation.required'),
  maxLength: {
    value: 100,
    message: i18n.t('validation.invalidEmailFormat'),
  },
  pattern: {
    // 管理者登録だけでなく、管理者画面でのログインやパスワードを忘れた時の入力フォームにもこれを使用しているため、gmo-research.ai,jpとも許可します。
    value: /^[A-Za-z0-9]{1}[A-Za-z0-9+_.-]*@gmo-research\.(jp|ai)$/,
    message: i18n.t('validation.invalidFormat'),
  },
};

// ログイン時のバリデーションは悪意持った人にルールは提示しない様にminLengthのバリデーションはしない
export const signinPasswordValidationRule = {
  required: i18n.t('validation.required'),
  // input側で最大文字数制限されているが、developer tool等での改竄されても大丈夫なように設定 メッセージは詳しく提示しない様にinvalidFormat
  maxLength: {
    value: 24,
    message: i18n.t('validation.invalidFormat'),
  },
  pattern: {
    value: /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])[a-zA-Z0-9]+$/,
    message: i18n.t('validation.invalidFormat'),
  },
};

// signin以外の通常のパスワードバリデーション
export const passwordValidationRule = {
  required: i18n.t('validation.required'),
  minLength: {
    value: 8,
    message: i18n.t('validation.minXToMaxY', {
      min: 8,
      max: 24,
    }),
  },
  // input側で最大文字数制限されているが、developer tool等での改竄されても大丈夫なように設定
  maxLength: {
    value: 24,
    message: i18n.t('validation.minXToMaxY', {
      min: 8,
      max: 24,
    }),
  },
  pattern: {
    value: /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])[a-zA-Z0-9]+$/,
    message: i18n.t('validation.invalidFormat'),
  },
};

export const userNameValidationRule = {
  required: i18n.t('validation.required'),
  maxLength: {
    value: 10,
    message: i18n.t('validation.maxLengthText', {
      length: 10,
    }),
  },
  // 全角ひらがな、全角カタカナ、全角漢字、(CJK統合漢字+々)のみ @See https://stabucky.com/wp/archives/7594
  pattern: {
    value: /^[ぁ-んァ-ヶー\u4E00-\u9FFF\u3005]*$/,
    message: i18n.t('validation.includeProhibitedCharacters'),
  },
};

export const userKanaNameValidationRule = {
  required: i18n.t('validation.required'),
  pattern: {
    value: /^[ァ-ヶー]*$/,
    message: i18n.t('validation.invalidKanaFormat'),
  },
};

export const adminNameValidationRule = {
  required: i18n.t('validation.required'),
  maxLength: {
    value: 20,
    message: i18n.t('validation.maxLengthText', {
      length: 20,
    }),
  },
  validate: {
    utf8mb4: (value) => validateUtf8mb4(value) || i18n.t('validation.includeUtf8mb4'),
  },
};

export const companyNameValidationRule = {
  required: i18n.t('validation.required'),
  maxLength: {
    value: 80,
    message: i18n.t('validation.maxLengthText', { length: 80 }),
  },
  validate: {
    utf8mb4: (value) => validateUtf8mb4(value) || i18n.t('validation.includeUtf8mb4'),
  },
};

export const personInChargeValidationRule = {
  required: i18n.t('validation.required'),
  maxLength: {
    value: 50,
    message: i18n.t('validation.maxLengthText', { length: 50 }),
  },
  validate: {
    utf8mb4: (value) => validateUtf8mb4(value) || i18n.t('validation.includeUtf8mb4'),
  },
};

export const invoiceRecipientNameValidationRule = {
  required: i18n.t('validation.required'),
  maxLength: {
    value: 50,
    message: i18n.t('validation.maxLengthText', { length: 50 }),
  },
  validate: {
    utf8mb4: (value) => validateUtf8mb4(value) || i18n.t('validation.includeUtf8mb4'),
  },
};

export const departmentValidationRule = {
  required: i18n.t('validation.required'),
  maxLength: {
    value: 100,
    message: i18n.t('validation.maxLengthText', { length: 100 }),
  },
  validate: {
    utf8mb4: (value) => validateUtf8mb4(value) || i18n.t('validation.includeUtf8mb4'),
  },
};

export const zipCodeValidationRule = {
  required: i18n.t('validation.required'),
  pattern: {
    value: /^([0-9]{7})$/,
    message: i18n.t('validation.numberWithJustLength', { length: 7 }),
  },
};

export const prefValidationRule = {
  required: i18n.t('validation.required'),
  validate: {
    validContent: (value) =>
      PREFECTURE_NAMES.includes(value) || i18n.t('validation.invalidPrefectureName'),
  },
};

export const requiredAddressValidationRule = {
  required: i18n.t('validation.required'),
  maxLength: {
    value: 128,
    message: i18n.t('validation.maxLengthText', { length: 128 }),
  },
  validate: {
    utf8mb4: (value) => validateUtf8mb4(value) || i18n.t('validation.includeUtf8mb4'),
  },
};

export const utf8mb4ValidationRule = {
  validate: {
    utf8mb4: (value) => validateUtf8mb4(value) || i18n.t('validation.includeUtf8mb4'),
  },
};

export const phoneValidationRule = {
  required: i18n.t('validation.required'),
  pattern: {
    value: /^[0-9]+$/,
    message: i18n.t('validation.numMinXToMaxY', {
      min: 10,
      max: 11,
    }),
  },
  minLength: {
    value: 10,
    message: i18n.t('validation.numMinXToMaxY', {
      min: 10,
      max: 11,
    }),
  },
};

export const antisocialCheckNotesValidationRule = {
  maxLength: {
    value: 1000,
    message: i18n.t('validation.maxLengthText', { length: 1000 }),
  },
  validate: {
    utf8mb4: (value) => validateUtf8mb4(value) || i18n.t('validation.includeUtf8mb4'),
  },
};

export const receiptRequestNameValidationRule = {
  required: i18n.t('validation.required'),
  maxLength: {
    value: 100,
    message: i18n.t('validation.maxLengthText', { length: 100 }),
  },
  validate: {
    utf8mb4: (value) => validateUtf8mb4(value) || i18n.t('validation.includeUtf8mb4'),
  },
};
